import { RoomIdentities } from '../protocol/roomTopics';
import type { AssistantPresence, WearerAssistantHint } from '../protocol/presenceMessages';

/** One assistant as the wearer sees them (Plan-01 step 4). */
export interface AssistantEntry {
  /** Room identity (`assistant:<slug>#<rand4>`). */
  readonly identity: string;
  /** Presence name -> token name -> identity slug, in that order. */
  displayName: string;
  /** Declared observer mode (presence) - no AV to control, still listed (RA-C5). */
  isObserver: boolean;
  videoPublished: boolean;
  videoMutedByPublisher: boolean;
  audioPublished: boolean;
  audioMutedByPublisher: boolean;
  /** Wearer's LOCAL hide (video unsubscribed - D5). Echoed on presence. */
  hiddenByWearer: boolean;
  /** Wearer's LOCAL mute (audio unsubscribed - D5). Echoed on presence. */
  mutedByWearer: boolean;
  /** A presence message has been received for this assistant. */
  hasPresence: boolean;
}

/** An unmuted video publication exists - the panel can show live video. */
export function hasLiveVideo(entry: AssistantEntry): boolean {
  return entry.videoPublished && !entry.videoMutedByPublisher;
}

/** An unmuted audio publication exists. */
export function hasLiveAudio(entry: AssistantEntry): boolean {
  return entry.audioPublished && !entry.audioMutedByPublisher;
}

/** Is this participant an assistant (not vision, not the wearer)? */
export function isAssistantParticipant(identity: string, metadata: string | null | undefined): boolean {
  if (!identity || identity === RoomIdentities.wearer || identity === RoomIdentities.vision) return false;
  // Metadata is the authoritative vision marker (consoles filter on it too);
  // a renamed vision bot still never reaches the roster.
  return !metadata || !metadata.includes('"role":"vision"');
}

/** The human slug of an assistant identity (`assistant:jane#4f2a` -> `jane`). */
export function slugFromIdentity(identity: string): string {
  if (!identity) return '';
  const value = identity.startsWith(RoomIdentities.assistantPrefix)
    ? identity.slice(RoomIdentities.assistantPrefix.length)
    : identity;
  const hash = value.indexOf('#');
  return hash > 0 ? value.slice(0, hash) : value;
}

type RosterListener = (entry: AssistantEntry) => void;

/**
 * The assistant roster reducer (Plan-01 step 4, RA-C5): consumes room
 * participant/track events and assistant `presence` messages, holds the
 * wearer's per-assistant hide/mute state (D5), and answers with the hints the
 * `wearerState` broadcast echoes back. Pure state - the service layer feeds it
 * and forwards its events; it persists nothing. Port of `AssistantRoster.cs`.
 *
 * Filtering: the vision participant (identity or `{"role":"vision"}` metadata)
 * and the wearer itself never enter the roster - same rule the console applies.
 */
export class AssistantRoster {
  private readonly entriesById = new Map<string, AssistantEntry>();
  private readonly joined = new Set<RosterListener>();
  private readonly left = new Set<RosterListener>();
  private readonly changed = new Set<RosterListener>();

  /** An assistant joined (first sight of the identity). */
  onJoined(listener: RosterListener): () => void {
    this.joined.add(listener);
    return () => this.joined.delete(listener);
  }

  /** An assistant left (participant disconnected, or the roster cleared). */
  onLeft(listener: RosterListener): () => void {
    this.left.add(listener);
    return () => this.left.delete(listener);
  }

  /** An assistant's AV/observer/hide/mute state changed. */
  onChanged(listener: RosterListener): () => void {
    this.changed.add(listener);
    return () => this.changed.delete(listener);
  }

  get count(): number {
    return this.entriesById.size;
  }

  /** Join-ordered snapshot (stable for menu/panel layout). A fresh array per read. */
  get entries(): readonly AssistantEntry[] {
    return [...this.entriesById.values()];
  }

  get(identity: string): AssistantEntry | undefined {
    return this.entriesById.get(identity);
  }

  /** The wearer's per-assistant hints for the presence echo (D5). */
  wearerHints(): WearerAssistantHint[] {
    return this.entries.map((entry) => ({
      identity: entry.identity,
      hidden: entry.hiddenByWearer,
      muted: entry.mutedByWearer,
    }));
  }

  /** A participant connected. No-op (false) for vision/wearer/duplicates. */
  applyParticipantJoined(identity: string, name: string, metadata: string): boolean {
    if (!isAssistantParticipant(identity, metadata) || this.entriesById.has(identity)) return false;
    const entry = newEntry(identity, name || slugFromIdentity(identity));
    this.entriesById.set(identity, entry);
    this.joined.forEach((listener) => listener(entry));
    return true;
  }

  /** A participant disconnected. False when it was not on the roster. */
  applyParticipantLeft(identity: string): boolean {
    const entry = identity ? this.entriesById.get(identity) : undefined;
    if (!entry) return false;
    this.entriesById.delete(identity);
    this.left.forEach((listener) => listener(entry));
    return true;
  }

  /** A remote publication appeared/disappeared/changed publisher-mute. */
  applyTrackChange(identity: string, isAudio: boolean, published: boolean, mutedByPublisher: boolean): boolean {
    const entry = identity ? this.entriesById.get(identity) : undefined;
    if (!entry) return false;

    let changed: boolean;
    if (isAudio) {
      changed = entry.audioPublished !== published || entry.audioMutedByPublisher !== mutedByPublisher;
      entry.audioPublished = published;
      entry.audioMutedByPublisher = mutedByPublisher;
    } else {
      changed = entry.videoPublished !== published || entry.videoMutedByPublisher !== mutedByPublisher;
      entry.videoPublished = published;
      entry.videoMutedByPublisher = mutedByPublisher;
    }

    if (changed) this.changed.forEach((listener) => listener(entry));
    return changed;
  }

  /**
   * An assistant's `presence` message arrived: name/observer intent. Presence
   * may precede the participant event after a reconnect race - unknown
   * identities create their entry (as a join).
   */
  applyPresence(presence: AssistantPresence): boolean {
    if (!isAssistantParticipant(presence.id, null)) return false;

    const name = presence.name || slugFromIdentity(presence.id);
    let entry = this.entriesById.get(presence.id);
    if (!entry) {
      entry = newEntry(presence.id, name);
      entry.isObserver = presence.observer;
      entry.hasPresence = true;
      this.entriesById.set(presence.id, entry);
      this.joined.forEach((listener) => listener(entry!));
      return true;
    }

    const changed = entry.displayName !== name || entry.isObserver !== presence.observer || !entry.hasPresence;
    entry.displayName = name;
    entry.isObserver = presence.observer;
    entry.hasPresence = true;
    if (changed) this.changed.forEach((listener) => listener(entry!));
    return changed;
  }

  /** Wearer hides/shows this assistant's video (D5 - local only). */
  setHidden(identity: string, hidden: boolean): boolean {
    const entry = identity ? this.entriesById.get(identity) : undefined;
    if (!entry || entry.hiddenByWearer === hidden) return false;
    entry.hiddenByWearer = hidden;
    this.changed.forEach((listener) => listener(entry));
    return true;
  }

  /** Wearer mutes/unmutes this assistant's audio (D5 - local only). */
  setMuted(identity: string, muted: boolean): boolean {
    const entry = identity ? this.entriesById.get(identity) : undefined;
    if (!entry || entry.mutedByWearer === muted) return false;
    entry.mutedByWearer = muted;
    this.changed.forEach((listener) => listener(entry));
    return true;
  }

  /** Room dropped: everyone leaves (left per entry), wearer hints reset with them. */
  clear(): void {
    if (this.entriesById.size === 0) return;
    const leaving = [...this.entriesById.values()];
    this.entriesById.clear();
    leaving.forEach((entry) => this.left.forEach((listener) => listener(entry)));
  }
}

function newEntry(identity: string, displayName: string): AssistantEntry {
  return {
    identity,
    displayName,
    isObserver: false,
    videoPublished: false,
    videoMutedByPublisher: false,
    audioPublished: false,
    audioMutedByPublisher: false,
    hiddenByWearer: false,
    mutedByWearer: false,
    hasPresence: false,
  };
}
